import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import sessionSecurity from '../utils/sessionSecurity';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Session timeout: 30 minutes of inactivity
  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

  // Logout function
  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('lastActivity');
    sessionSecurity.clearSession();
    delete axios.defaults.headers.common['Authorization'];
  };

  // Check session timeout
  useEffect(() => {
    const checkTimeout = () => {
      const lastActivityTime = parseInt(localStorage.getItem('lastActivity') || Date.now());
      const currentTime = Date.now();
      
      if (user && (currentTime - lastActivityTime > SESSION_TIMEOUT)) {
        logout();
        setError('Session expired due to inactivity. Please login again.');
      }
    };

    // Check every minute
    const interval = setInterval(checkTimeout, 60000);
    
    return () => clearInterval(interval);
  }, [user]);

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      const now = Date.now();
      setLastActivity(now);
      localStorage.setItem('lastActivity', now.toString());
    };

    // Update activity on user interactions
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, []);

  // Set up axios interceptor for handling unauthorized errors
  useEffect(() => {
    // Add a response interceptor
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle 401 Unauthorized errors
        if (error.response && error.response.status === 401) {
          // Clear user data and redirect to login
          setUser(null);
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
          
          // Only show the error if it's not a token failure during normal operation
          if (error.config.url !== '/api/auth/profile') {
            setError('Your session has expired. Please log in again.');
          }
        }
        return Promise.reject(error);
      }
    );

    // Clean up interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Load user from localStorage on startup
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const lastActivityTime = parseInt(localStorage.getItem('lastActivity') || Date.now());
    const currentTime = Date.now();
    
    if (storedUser) {
      // Check if session has expired
      if (currentTime - lastActivityTime > SESSION_TIMEOUT) {
        // Session expired, clear everything
        localStorage.removeItem('user');
        localStorage.removeItem('lastActivity');
        sessionSecurity.clearSession();
        setError('Session expired. Please login again.');
        setLoading(false);
        return;
      }
      
      // Validate session security
      if (!sessionSecurity.isValidSession()) {
        // Invalid session, clear everything
        localStorage.removeItem('user');
        localStorage.removeItem('lastActivity');
        sessionSecurity.clearSession();
        setError('Invalid session. Please login again.');
        setLoading(false);
        return;
      }
      
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      
      // Update last activity
      localStorage.setItem('lastActivity', currentTime.toString());
      
      // Set default auth header for all requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${parsedUser.token}`;
      if (parsedUser.id) {
        axios.defaults.headers.common['X-User-ID'] = parsedUser.id.toString();
      }
      
      // Only refresh user data for staff, not patients
      if (parsedUser.role !== 'patient') {
        const refreshUserData = async () => {
          try {
            const [userRes, permissionsRes] = await Promise.all([
              axios.get('/api/users.php?id=' + parsedUser.id),
              axios.get('/api/user-permissions.php?userId=' + parsedUser.id)
            ]);
            
            if (userRes.data && !userRes.data.error) {
              const refreshedUser = { 
                ...userRes.data, 
                token: parsedUser.token,
                doctorId: userRes.data.doctorId || parsedUser.doctorId,
                permissions: permissionsRes.data?.permissions || {}
              };
              setUser(refreshedUser);
              localStorage.setItem('user', JSON.stringify(refreshedUser));
            }
          } catch (err) {
            console.error('Error refreshing user data:', err);
          }
        };
        
        refreshUserData();
      }
    }
    
    setLoading(false);
  }, []);

  // Login function
  const login = async (username, password, loginType = 'staff') => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Attempting login with:', { username, loginType });
      
      const res = await axios.post('/api/login.php', { username, password, loginType });
      
      console.log('Login response:', res.data);
      
      if (res.data && res.data.token) {
        // Initialize session security
        sessionSecurity.initializeSession();
        
        // Load user permissions
        let userPermissions = {};
        try {
          const permRes = await axios.get('/api/user-permissions.php?userId=' + res.data.user.id, {
            headers: { 'Authorization': `Bearer ${res.data.token}` }
          });
          userPermissions = permRes.data?.permissions || {};
        } catch (err) {
          console.log('No permissions found for user');
        }
        
        // Flatten the response - merge user object with token and permissions
        const userData = {
          ...res.data.user,
          token: res.data.token,
          permissions: userPermissions
        };
        
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('lastActivity', Date.now().toString());
        
        // Set default auth header for all requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
        
        setLoading(false);
        return true;
      } else {
        setError('Invalid response from server');
        setLoading(false);
        return false;
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.error || 'Login failed. Please try again.');
      setLoading(false);
      return false;
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await axios.post('/api/users.php', userData);
      
      // Don't auto-login after registration, just return success
      setLoading(false);
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
      setLoading(false);
      return false;
    }
  };

  // Logout function (moved up for use in useEffect)
  // Already defined above

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('updateProfile called with:', userData);
      
      // Normal profile update via API
      console.log('Updating user profile via API with:', userData);
      
      // Add the current user ID to ensure we're only updating this user
      const dataToSend = {
        ...userData,
        userId: user.id
      };
      
      const res = await axios.put('/api/users.php', dataToSend, {
        headers: {
          'X-User-ID': user.id ? user.id.toString() : ''
        }
      });
      
      // Update user data
      const updatedUser = { 
        ...user, 
        ...res.data,
      };
      
      console.log('Updated user data (API response):', updatedUser);
      
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Profile update error:', err);
      setError(err.response?.data?.error || 'Profile update failed');
      setLoading(false);
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        register,
        logout,
        updateProfile,
        setError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 